import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/admin';
import { Trophy, Plus, Calendar, DollarSign, Sparkles, Filter, Users, ChevronLeft, ChevronRight, Trash2, Info, Loader2 } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Se il DB salva in UTC: data+ora inseriti = UTC → restituisce la stringa in ora italiana (CET/CEST a seconda di solstizi/equinozi). */
function utcToItalyLabel(dateStr, timeStr, defaultTime = '00:00', locale = 'it-IT') {
  if (!dateStr) return null;
  const time = timeStr || defaultTime;
  const utc = new Date(`${dateStr}T${time}:00.000Z`);
  return utc.toLocaleString(locale, {
    timeZone: 'Europe/Rome',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

const AdminGameWeeks = () => {
  const navigate = useNavigate();
  const { locale } = useLanguage();
  const [gameweeks, setGameweeks] = useState([]);
  const [allGameweeks, setAllGameweeks] = useState([]);
  const [loading, setLoading] = useState(true);
  /** Sync SportMonks + recalc score in sequenza (un solo flusso admin). */
  const [fullRefreshGwId, setFullRefreshGwId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateMultipleModal, setShowCreateMultipleModal] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [fixingXpCards, setFixingXpCards] = useState(false);
  const [xpCardsModal, setXpCardsModal] = useState({
    open: false,
    gameweekId: '',
    loading: false,
    error: null,
    cards: []
  });
  const [paidPrizesModal, setPaidPrizesModal] = useState({
    open: false,
    gameweekName: '',
    result: null
  });
  /** Anteprima dry-run prima di POST pay-prizes (una GET calculate per torneo). */
  const [payPrizesPreviewModal, setPayPrizesPreviewModal] = useState({
    open: false,
    loading: false,
    paying: false,
    error: null,
    gameweekId: null,
    gameweekName: '',
    totalParticipants: 0,
    distributions: []
  });
  const ITEMS_PER_PAGE = 6;

  
  // Form state for single gameweek
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    categoriesEnabled: ['basic', 'plus', 'rare', 'epic', 'unique'],
    allowLegends: true,
    allowDivinities: false,
    competitions: []
  });
  
  // Form state for multiple gameweeks
  const [multipleGameweeks, setMultipleGameweeks] = useState([
    {
      name: '',
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      categoriesEnabled: ['basic', 'plus', 'rare', 'epic', 'unique'],
      allowLegends: true,
      allowDivinities: false
    }
  ]);

  useEffect(() => {
    setCurrentPage(0);
    loadGameWeeks();
  }, [showActiveOnly]);

  useEffect(() => {
    if (allGameweeks.length > 0) {
      const start = currentPage * ITEMS_PER_PAGE;
      setGameweeks(allGameweeks.slice(start, start + ITEMS_PER_PAGE));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const loadGameWeeks = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getGameWeeks();
      console.log('🔍 [Admin GameWeeks] Response received:', response);
      
      if (response && response.success !== false) {
        let displayGameweeks = [];
        
        // Check if backend returns gameweeks array (new format)
        if (response.gameweeks && Array.isArray(response.gameweeks)) {
          console.log(`🔍 [Admin GameWeeks] Found ${response.gameweeks.length} gameweeks in response`);
          
          // Use the gameweeks array directly (prefer DB status)
          displayGameweeks = response.gameweeks.map(gw => ({
            id: gw.id,
            name: gw.name,
            startDate: gw.startDate,
            endDate: gw.endDate,
            tournaments: gw.tournaments || [],
            status: gw.status || 'upcoming'
          }));
          
          console.log(`🔍 [Admin GameWeeks] Processed ${displayGameweeks.length} gameweeks:`, displayGameweeks.map(gw => ({ name: gw.name, status: gw.status, tournaments: gw.tournaments.length })));
        } else {
          // Fallback: group tournaments by name (old format)
          const tournaments = Array.isArray(response) ? response : (response.tournaments || []);
          console.log('🔍 [Admin GameWeeks] Loaded gameweeks from backend (legacy format):', tournaments.length);
          
          // Raggruppa le gameweek per nome base
          const groupedGameweeks = tournaments.reduce((acc, gw) => {
            const nameParts = gw.name.split(' - ');
            const gameweekName = nameParts.length >= 2 ? nameParts.slice(0, -1).join(' - ') : nameParts[0];
            
            if (!acc[gameweekName]) {
              acc[gameweekName] = {
                name: gameweekName,
                tournaments: [],
                startDate: gw.start_date || gw.gameweek_start_date,
                endDate: gw.end_date || gw.gameweek_end_date,
                status: gw.status,
                id: gw.gameweek_id || gw.id
              };
            }
            acc[gameweekName].tournaments.push(gw);
            
            if (gw.status === 'in_progress') {
              acc[gameweekName].status = 'in_progress';
            }
            
            return acc;
          }, {});
          
          displayGameweeks = Object.values(groupedGameweeks);
        }
        
        // Filter by active only if needed
        const filteredGameweeks = showActiveOnly 
          ? displayGameweeks.filter(gw => {
              const now = new Date();
              const startDate = new Date(gw.startDate);
              const endDate = new Date(gw.endDate);
              return gw.status === 'in_progress' && now >= startDate && now <= endDate;
            })
          : displayGameweeks;
        
        // Sort by start date (newest first)
        filteredGameweeks.sort((a, b) => {
          const dateA = new Date(a.startDate);
          const dateB = new Date(b.startDate);
          return dateB.getTime() - dateA.getTime();
        });
        
        console.log(`🔍 [Admin GameWeeks] Final filtered gameweeks: ${filteredGameweeks.length}`, filteredGameweeks.map(gw => ({ name: gw.name, status: gw.status })));
        
        setAllGameweeks(filteredGameweeks);
        const start = currentPage * ITEMS_PER_PAGE;
        setGameweeks(filteredGameweeks.slice(start, start + ITEMS_PER_PAGE));
      } else {
        setGameweeks([]);
        setAllGameweeks([]);
      }
    } catch (error) {
      console.error('Error loading gameweeks:', error);
      // Set empty array on error to prevent crashes
      setGameweeks([]);
      setAllGameweeks([]);
    } finally {
      setLoading(false);
    }
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-500/20 text-blue-400';
      case 'open': return 'bg-green-500/20 text-green-400';
      case 'in_progress': return 'bg-green-500/20 text-green-400 border-2 border-green-400';
      case 'active': return 'bg-green-500/20 text-green-400';
      case 'closed': return 'bg-gray-500/20 text-gray-400';
      case 'close': return 'bg-gray-500/20 text-gray-400';
      case 'pending_payment': return 'bg-purple-500/20 text-purple-400';
      case 'completed': return 'bg-purple-500/20 text-purple-400';
      case 'cancelled': return 'bg-red-500/20 text-red-400';
      case 'verification': return 'bg-yellow-500/20 text-yellow-400';
      case 'paid': return 'bg-purple-500/20 text-purple-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'in_progress':
        return 'PROGRESS';
      case 'pending_payment':
        return 'PENDING PAYMENT';
      case 'close':
      case 'closed':
        return 'CLOSE';
      case 'upcoming':
        return 'UPCOMING';
      case 'open':
        return 'OPEN';
      default:
        return String(status || '').toUpperCase();
    }
  };

  const isActive = (gameweek) => {
    const now = new Date();
    const startDate = new Date(gameweek.startDate);
    const endDate = new Date(gameweek.endDate);
    return gameweek.status === 'in_progress' && now >= startDate && now <= endDate;
  };

  const formatAmount = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '0.00';
    return n.toFixed(2);
  };

  const loadGameweekTeamCardsXp = async (gameweekId) => {
    if (!gameweekId) {
      setXpCardsModal((prev) => ({ ...prev, cards: [], error: null, loading: false }));
      return;
    }
    setXpCardsModal((prev) => ({ ...prev, loading: true, error: null, cards: [] }));
    try {
      const response = await adminApi.getGameweekTeamCardsXp(gameweekId);
      const cards = response?.cards ?? response?.data?.cards ?? [];
      setXpCardsModal((prev) => ({
        ...prev,
        loading: false,
        cards: Array.isArray(cards) ? cards : [],
        error: null
      }));
    } catch (error) {
      console.error('Error loading gameweek team cards XP:', error);
      setXpCardsModal((prev) => ({
        ...prev,
        loading: false,
        cards: [],
        error: error.response?.data?.error || error.message || 'Errore caricamento card'
      }));
    }
  };

  const openXpCardsModal = () => {
    const defaultId = allGameweeks.length > 0 ? String(allGameweeks[0].id) : '';
    setXpCardsModal({
      open: true,
      gameweekId: defaultId,
      loading: !!defaultId,
      error: null,
      cards: []
    });
    if (defaultId) {
      loadGameweekTeamCardsXp(defaultId);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold flex items-center">
          <Trophy className="w-8 h-8 mr-3 text-yellow-400" />
          GameWeek Management
        </h1>
        <div className="flex items-center space-x-3">
          <button
            onClick={openXpCardsModal}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg flex items-center space-x-2"
          >
            <Sparkles className="w-4 h-4" />
            <span>Aggiorna XP cards</span>
          </button>
          <button
            onClick={() => {
              setShowActiveOnly(!showActiveOnly);
            }}
            className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
              showActiveOnly 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-gray-700 hover:bg-gray-600'
            }`}
          >
            <Filter className="w-4 h-4" />
            <span>{showActiveOnly ? 'Show All' : 'Show Active Only'}</span>
          </button>
          <button
            onClick={async () => {
              if (!window.confirm('⚠️ WARNING: This will delete ALL gameweeks and ALL their tournaments!\n\nThis action cannot be undone!\n\nAre you absolutely sure?')) {
                return;
              }
              try {
                setLoading(true);
                const result = await adminApi.deleteAllGameWeeks();
                alert(`Successfully deleted ${result.deleted} gameweek(s) and ${result.tournamentsDeleted} tournament(s)!`);
                loadGameWeeks();
              } catch (error) {
                console.error('Error deleting all gameweeks:', error);
                alert('Error deleting all gameweeks: ' + (error.response?.data?.error || error.message));
              } finally {
                setLoading(false);
              }
            }}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg flex items-center space-x-2"
          >
            <Trash2 className="w-4 h-4" />
            <span>Delete All</span>
          </button>
          <button
            onClick={() => setShowCreateMultipleModal(true)}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create Multiple</span>
          </button>
          <button
            onClick={() => {
              setFormData({
                name: '',
                startDate: '',
                endDate: '',
                startTime: '',
                endTime: '',
                categoriesEnabled: ['basic', 'plus', 'rare', 'epic', 'unique'],
                allowLegends: true,
                allowDivinities: false,
                competitions: []
              });
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create GameWeek</span>
          </button>
        </div>
      </div>

      {/* GameWeeks List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading gameweeks...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {gameweeks.map((gw) => {
            const tournaments = gw.tournaments || [];
            const active = isActive(gw);
            const totalPrizePool = tournaments.reduce((sum, t) => sum + (parseFloat(t.prize_pool) || 0), 0);
            const totalParticipants = tournaments.reduce((sum, t) => sum + (parseInt(t.participants) || 0), 0);
            const canPayPrizes = gw.status === 'closed' || gw.status === 'pending_payment';
            const isClosedGameweek = ['close', 'closed', 'completed', 'paid'].includes(String(gw.status || '').toLowerCase());

            return (
            <div 
              key={gw.id} 
              className={`bg-gray-800 rounded-lg p-6 border ${
                active 
                  ? 'border-green-500 border-2 shadow-lg shadow-green-500/20' 
                  : 'border-gray-700'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <h3 className="text-xl font-bold">{gw.name}</h3>
                  {active && (
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  )}
                </div>
                <span className={`px-3 py-1 text-xs rounded-full font-semibold ${getStatusColor(gw.status)}`}>
                  {getStatusLabel(gw.status)}
                </span>
              </div>
              <div className="space-y-2 text-sm mb-3">
                <div className="flex items-center text-gray-400">
                  <Calendar className="w-4 h-4 mr-2" />
                  {new Date(gw.startDate).toLocaleDateString()} - {new Date(gw.endDate).toLocaleDateString()}
                </div>
                <div className="flex items-center text-gray-400">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Total Prize Pool: {totalPrizePool.toFixed(2)} DIOS
                </div>
                <div className="text-gray-400">
                  <Users className="w-4 h-4 inline mr-2" />
                  Total Participants: {totalParticipants}
                </div>
                <div className="text-gray-400">
                  Tournaments: {tournaments.length} (Basic, Plus, Rare, Epic, Unique)
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2 items-stretch">
                <button
                  onClick={() => navigate(`/admin/gameweeks/${gw.id}`)}
                  className="flex-1 min-w-[10rem] shrink-0 whitespace-nowrap px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm"
                >
                  View Details
                </button>
                {!isClosedGameweek && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (
                        !window.confirm(
                          `Aggiornare TUTTO per «${gw.name}»?\n\n` +
                            'Flusso automatico:\n' +
                            '1) Sync SportMonks → tooltip/statistiche\n' +
                            '2) Recalc score squadre (parte appena finisce il sync)\n\n' +
                            'Per ridurre chiamate a SportMonks viene fatto solo 1 sync.'
                        )
                      ) {
                        return;
                      }
                      setFullRefreshGwId(gw.id);
                      const gwId = gw.id;
                      const pollMs = 4000;
                      const maxRounds = 450;
                      try {
                        const syncDetailFrom = (r) =>
                          [
                            r?.fixtures_fetched != null ? `Fixture ${r.fixtures_fetched}/${r.fixtures_requested ?? 0}` : '',
                            r?.rows_updated != null ? `righe ≈ ${r.rows_updated}` : '',
                          ]
                            .filter(Boolean)
                            .join(', ');

                        const start = await adminApi.refreshTooltipAndScoreForGameWeek(gwId);
                        const started = start?.data || start;
                        if (started?.success === false) {
                          alert('Avvio refresh fallito: ' + (started?.error || 'errore'));
                          return;
                        }

                        let finalSync = null;
                        let finalRecalc = null;
                        await sleep(800);

                        for (let i = 0; i < maxRounds; i++) {
                          const stRes = await adminApi.getRefreshTooltipAndScoreStatus(gwId);
                          const st = stRes?.data || stRes;
                          if (st?.status === 'idle') {
                            finalSync = st?.result?.sync || st?.sync?.result || null;
                            finalRecalc = st?.result?.recalc || st?.recalc?.result || null;
                            break;
                          }
                          await sleep(pollMs);
                        }

                        if (!finalSync && started?.sync) {
                          finalSync = started.sync;
                        }
                        if (!finalRecalc && started?.recalc) {
                          finalRecalc = started.recalc;
                        }

                        if (!finalSync) {
                          alert('Timeout sync: esito non chiaro. Controlla i log events/payment.');
                          return;
                        }
                        if (finalSync?.success === false) {
                          alert('Sync fallito: ' + (finalSync?.error || 'errore'));
                          return;
                        }
                        if (!finalRecalc) {
                          alert(`Sync OK (${syncDetailFrom(finalSync) || 'ok'}). Timeout recalc: controlla i log.`);
                          loadGameWeeks();
                          return;
                        }
                        if (finalRecalc?.success === false || finalRecalc?.error) {
                          alert(`Sync OK (${syncDetailFrom(finalSync) || 'ok'}). Recalc fallito: ${finalRecalc?.error || 'errore'}`);
                          loadGameWeeks();
                          return;
                        }

                        const syncDetail = syncDetailFrom(finalSync) || (finalSync?.message ? String(finalSync.message) : 'ok');
                        const recMsg = `${finalRecalc?.teams_processed ?? 0} team processati.`;
                        alert(`Fatto.\n• Sync: ${syncDetail}\n• Recalc: ${recMsg}\n\nRicarica la formazione nel gioco.`);
                        loadGameWeeks();
                      } catch (e) {
                        console.error('Full refresh gameweek:', e);
                        alert('Errore: ' + (e?.response?.data?.error || e?.message));
                      } finally {
                        setFullRefreshGwId(null);
                      }
                    }}
                    disabled={fullRefreshGwId != null}
                    className="shrink-0 px-3 py-2 bg-emerald-700 hover:bg-emerald-600 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 min-w-[11rem] border border-emerald-500/40"
                    title="SportMonks → statistiche/tooltip (player_scores), poi ricalcolo punteggi sulle squadre"
                  >
                    {fullRefreshGwId === gw.id ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                        <span>Sync + recalc…</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 shrink-0" />
                        <span>Tutto (tooltip + score)</span>
                      </>
                    )}
                  </button>
                )}
                {!isClosedGameweek && canPayPrizes && (
                  <button
                    type="button"
                    onClick={() => {
                      setPayPrizesPreviewModal({
                        open: true,
                        loading: true,
                        paying: false,
                        error: null,
                        gameweekId: gw.id,
                        gameweekName: gw.name,
                        totalParticipants,
                        distributions: []
                      });
                      const tournaments = gw.tournaments || [];
                      if (tournaments.length === 0) {
                        setPayPrizesPreviewModal((prev) => ({
                          ...prev,
                          loading: false,
                          error: 'Nessun torneo in questa gameweek.'
                        }));
                        return;
                      }
                      (async () => {
                        try {
                          const rows = await Promise.all(
                            tournaments.map(async (t) => {
                              try {
                                const res = await adminApi.calculatePrizeDistribution(t.id);
                                const dist = res?.data?.distribution ?? res?.distribution;
                                return {
                                  tournamentId: t.id,
                                  tournamentName: dist?.tournamentName || t.name || `Tournament #${t.id}`,
                                  ok: true,
                                  distribution: dist
                                };
                              } catch (err) {
                                return {
                                  tournamentId: t.id,
                                  tournamentName: t.name || `Tournament #${t.id}`,
                                  ok: false,
                                  error: err.response?.data?.error || err.message || 'Calcolo fallito'
                                };
                              }
                            })
                          );
                          setPayPrizesPreviewModal((prev) => ({
                            ...prev,
                            loading: false,
                            distributions: rows
                          }));
                        } catch (e) {
                          console.error('Preview pay prizes:', e);
                          setPayPrizesPreviewModal((prev) => ({
                            ...prev,
                            loading: false,
                            error: e.response?.data?.error || e.message || 'Errore caricamento anteprima'
                          }));
                        }
                      })();
                    }}
                    className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm"
                  >
                    Pay Prizes
                  </button>
                )}
                {!isClosedGameweek && (
                <button
                  onClick={async () => {
                    if (!window.confirm(`Are you sure you want to delete "${gw.name}"?\n\nThis will delete:\n- The gameweek\n- All ${tournaments.length} tournaments\n- All tournament entries\n\nThis action cannot be undone!`)) {
                      return;
                    }
                    setDeleting(gw.id);
                    try {
                      await adminApi.deleteGameWeek(gw.id);
                      alert('GameWeek deleted successfully!');
                      loadGameWeeks();
                    } catch (error) {
                      console.error('Error deleting gameweek:', error);
                      alert('Error deleting gameweek: ' + (error.response?.data?.error || error.message));
                    } finally {
                      setDeleting(null);
                    }
                  }}
                  disabled={deleting === gw.id}
                  className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm disabled:opacity-50 flex items-center space-x-1"
                >
                  <Trash2 className="w-4 h-4" />
                  {deleting === gw.id ? 'Deleting...' : ''}
                </button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {!loading && gameweeks.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          {showActiveOnly 
            ? 'No active gameweeks found.'
            : 'No gameweeks found. Create gameweeks using the buttons above.'}
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && allGameweeks.length > ITEMS_PER_PAGE && (
        <div className="flex items-center justify-center space-x-4 mt-6">
          <button
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
              currentPage === 0
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Previous
          </button>
          <span className="text-white/90 text-sm font-medium">
            Page {currentPage + 1} of {Math.ceil(allGameweeks.length / ITEMS_PER_PAGE)}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(Math.ceil(allGameweeks.length / ITEMS_PER_PAGE) - 1, currentPage + 1))}
            disabled={currentPage >= Math.ceil(allGameweeks.length / ITEMS_PER_PAGE) - 1}
            className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
              currentPage >= Math.ceil(allGameweeks.length / ITEMS_PER_PAGE) - 1
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Next
            <ChevronRight className="w-5 h-5 ml-1" />
          </button>
        </div>
      )}

      {/* Modal XP cards per gameweek */}
      {xpCardsModal.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-xl">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-xl font-bold text-white">XP cards — team_cards</h3>
                <p className="text-sm text-gray-400 mt-0.5">
                  Tutte le card schierate nei tornei collegati alla gameweek selezionata
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setXpCardsModal({
                    open: false,
                    gameweekId: '',
                    loading: false,
                    error: null,
                    cards: []
                  })
                }
                className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm"
              >
                Chiudi
              </button>
            </div>

            <div className="p-4 border-b border-gray-700">
              <label className="block text-sm text-gray-300 mb-1.5">Gameweek</label>
              <select
                value={xpCardsModal.gameweekId}
                onChange={(e) => {
                  const id = e.target.value;
                  setXpCardsModal((prev) => ({ ...prev, gameweekId: id }));
                  loadGameweekTeamCardsXp(id);
                }}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
              >
                <option value="">— Seleziona gameweek —</option>
                {allGameweeks.map((gw) => (
                  <option key={gw.id} value={String(gw.id)}>
                    {gw.name} (#{gw.id})
                  </option>
                ))}
              </select>
            </div>

            <div className="p-4 overflow-y-auto flex-1 min-h-0">
              {!xpCardsModal.gameweekId && (
                <p className="text-gray-400 text-sm text-center py-8">Seleziona una gameweek per vedere le card.</p>
              )}

              {xpCardsModal.loading && (
                <div className="flex items-center gap-2 text-gray-300 py-8 justify-center">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span>Caricamento card…</span>
                </div>
              )}

              {xpCardsModal.error && !xpCardsModal.loading && (
                <p className="text-red-400 text-sm">{xpCardsModal.error}</p>
              )}

              {!xpCardsModal.loading &&
                !xpCardsModal.error &&
                xpCardsModal.gameweekId &&
                xpCardsModal.cards.length === 0 && (
                  <p className="text-gray-400 text-sm text-center py-8">
                    Nessuna card in team_cards per questa gameweek.
                  </p>
                )}

              {!xpCardsModal.loading && xpCardsModal.cards.length > 0 && (
                <>
                  <p className="text-xs text-gray-400 mb-2">
                    {xpCardsModal.cards.length} righe (stessa card può comparire in più tornei)
                  </p>
                  <div className="overflow-x-auto border border-gray-700 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-900/70 text-gray-300">
                        <tr>
                          <th className="text-left p-2.5 font-semibold">Card ID</th>
                          <th className="text-left p-2.5 font-semibold">Rarità</th>
                          <th className="text-right p-2.5 font-semibold">XP %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {xpCardsModal.cards.map((row, idx) => (
                          <tr
                            key={`${row.card_id}-${row.tournament_id}-${row.user_id}-${idx}`}
                            className="border-t border-gray-700/80 hover:bg-gray-700/30"
                          >
                            <td className="p-2.5 text-white font-mono tabular-nums">{row.card_id}</td>
                            <td className="p-2.5 text-white capitalize">{row.rarity}</td>
                            <td className="p-2.5 text-right text-emerald-300 font-mono tabular-nums">
                              {Number(row.xp_percentage).toFixed(2)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            <div className="p-4 border-t border-gray-700 flex justify-end gap-2">
              <button
                type="button"
                disabled={fixingXpCards || !xpCardsModal.gameweekId}
                onClick={async () => {
                  const gwId = xpCardsModal.gameweekId;
                  if (!gwId) return;
                  const zeroCount = xpCardsModal.cards.filter((r) => Number(r.xp_percentage) === 0).length;
                  const uniqueZero = new Set(
                    xpCardsModal.cards
                      .filter((r) => Number(r.xp_percentage) === 0)
                      .map((r) => r.card_id)
                  ).size;
                  if (
                    !window.confirm(
                      `Correggi le card schierate in questa gameweek con XP=0? (+0.5% XP, ${uniqueZero} card uniche, ${zeroCount} righe in tabella)`
                    )
                  ) {
                    return;
                  }
                  setFixingXpCards(true);
                  try {
                    const result = await adminApi.fixXpZeroForGameweek(gwId, false);
                    const res = result?.data?.result ?? {};
                    const fixed = res.fixed ?? 0;
                    const toFix = res.to_fix_count ?? 0;
                    const err = res.error;
                    if (err) {
                      alert('Errore fix XP: ' + err);
                    } else {
                      alert(
                        toFix > 0
                          ? `Corrette ${fixed} card su ${toFix} con XP=0 in questa gameweek.`
                          : 'Nessuna card con XP=0 da correggere in questa gameweek.'
                      );
                    }
                    loadGameweekTeamCardsXp(gwId);
                    loadGameWeeks();
                  } catch (error) {
                    console.error('Error fixing XP cards:', error);
                    alert('Errore fix XP: ' + (error.response?.data?.error || error.message));
                  } finally {
                    setFixingXpCards(false);
                  }
                }}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 rounded-lg text-sm disabled:opacity-50"
              >
                {fixingXpCards ? 'Fix in corso…' : 'Fix XP=0 (gameweek)'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Anteprima pagamento premi (dry-run) */}
      {payPrizesPreviewModal.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-xl">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-xl font-bold text-white">Anteprima pagamento premi</h3>
                <p className="text-sm text-gray-400 mt-0.5">{payPrizesPreviewModal.gameweekName}</p>
              </div>
              <button
                type="button"
                disabled={payPrizesPreviewModal.paying}
                onClick={() =>
                  !payPrizesPreviewModal.paying &&
                  setPayPrizesPreviewModal({
                    open: false,
                    loading: false,
                    paying: false,
                    error: null,
                    gameweekId: null,
                    gameweekName: '',
                    totalParticipants: 0,
                    distributions: []
                  })
                }
                className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm disabled:opacity-50"
              >
                Chiudi
              </button>
            </div>

            <div className="p-4 overflow-y-auto space-y-4 flex-1">
              <p className="text-xs text-amber-200/90 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2">
                Anteprima (solo lettura): nessun accredito finché non premi «Conferma e paga». I dati sono calcolati come al
                momento della richiesta.
              </p>

              {payPrizesPreviewModal.totalParticipants === 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-amber-200 text-sm">
                  Nessun partecipante nei tornei: al pagamento l&apos;intero prize pool della gameweek verrà inviato al
                  treasury (comportamento attuale del backend).
                </div>
              )}

              {payPrizesPreviewModal.loading && (
                <div className="flex items-center gap-2 text-gray-300 py-6 justify-center">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span>Caricamento anteprima…</span>
                </div>
              )}

              {payPrizesPreviewModal.error && !payPrizesPreviewModal.loading && (
                <p className="text-red-400 text-sm">{payPrizesPreviewModal.error}</p>
              )}

              {!payPrizesPreviewModal.loading &&
                payPrizesPreviewModal.distributions.map((row) => (
                  <div key={row.tournamentId} className="border border-gray-700 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-gray-900/70 border-b border-gray-700">
                      <p className="font-semibold text-white">{row.tournamentName}</p>
                      {!row.ok ? (
                        <p className="text-sm text-red-400 mt-1">{row.error}</p>
                      ) : (
                        <p className="text-xs text-gray-400 mt-1">
                          Partecipanti: {row.distribution?.participants ?? 0} — Pool:{' '}
                          {formatAmount(row.distribution?.totalPrizePool)} DIOS — Premiati:{' '}
                          {row.distribution?.prizeWinners ?? 0}
                          {Number(row.distribution?.remainingPrize) > 0 && (
                            <span className="text-amber-200/90">
                              {' '}
                              — Non assegnato: {formatAmount(row.distribution?.remainingPrize)} DIOS
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                    {row.ok && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-900/50 text-gray-300">
                            <tr>
                              <th className="text-left p-2">Rank</th>
                              <th className="text-left p-2">Utente</th>
                              <th className="text-left p-2">ID</th>
                              <th className="text-left p-2">Score</th>
                              <th className="text-left p-2">Premio</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(row.distribution?.distribution || []).length === 0 ? (
                              <tr>
                                <td className="p-3 text-gray-400" colSpan={5}>
                                  Nessuna riga di premio (pool vuoto o nessun classificato idoneo).
                                </td>
                              </tr>
                            ) : (
                              (row.distribution?.distribution || []).map((p, idx) => (
                                <tr key={`${row.tournamentId}-${p.userId}-${idx}`} className="border-t border-gray-700/70">
                                  <td className="p-2">{p.rank ?? '-'}</td>
                                  <td className="p-2">{p.username || '-'}</td>
                                  <td className="p-2 text-gray-400">{p.userId}</td>
                                  <td className="p-2">{p.score != null ? Number(p.score).toFixed(2) : '-'}</td>
                                  <td className="p-2 text-emerald-400">{formatAmount(p.prizeAmount)} DIOS</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
            </div>

            <div className="p-4 border-t border-gray-700 flex flex-wrap justify-end gap-2 bg-gray-900/40">
              <button
                type="button"
                disabled={payPrizesPreviewModal.paying}
                onClick={() =>
                  setPayPrizesPreviewModal({
                    open: false,
                    loading: false,
                    paying: false,
                    error: null,
                    gameweekId: null,
                    gameweekName: '',
                    totalParticipants: 0,
                    distributions: []
                  })
                }
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                type="button"
                disabled={
                  payPrizesPreviewModal.loading ||
                  payPrizesPreviewModal.paying ||
                  !!payPrizesPreviewModal.error ||
                  !payPrizesPreviewModal.gameweekId ||
                  payPrizesPreviewModal.distributions.length === 0 ||
                  payPrizesPreviewModal.distributions.some((d) => !d.ok)
                }
                onClick={async () => {
                  const gid = payPrizesPreviewModal.gameweekId;
                  const gname = payPrizesPreviewModal.gameweekName;
                  if (!gid) return;
                  if (
                    !window.confirm(
                      `Confermi il pagamento premi per «${gname}»?\n\nL'operazione accrediterà i vincitori (o invierà il pool al treasury se non ci sono partecipanti).`
                    )
                  ) {
                    return;
                  }
                  setPayPrizesPreviewModal((p) => ({ ...p, paying: true }));
                  try {
                    const res = await adminApi.payPrizesForGameWeek(gid);
                    const r = res?.data?.result || res?.result;
                    setPayPrizesPreviewModal({
                      open: false,
                      loading: false,
                      paying: false,
                      error: null,
                      gameweekId: null,
                      gameweekName: '',
                      totalParticipants: 0,
                      distributions: []
                    });
                    setPaidPrizesModal({
                      open: true,
                      gameweekName: gname,
                      result: r || null
                    });
                    loadGameWeeks();
                  } catch (err) {
                    console.error('Pay prizes for game week:', err);
                    alert('Errore: ' + (err.response?.data?.error || err.message));
                  } finally {
                    setPayPrizesPreviewModal((p) => ({ ...p, paying: false }));
                  }
                }}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {payPrizesPreviewModal.paying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Pagamento…
                  </>
                ) : (
                  'Conferma e paga'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Single GameWeek Modal */}
      {paidPrizesModal.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-xl font-bold">Prizes Paid - {paidPrizesModal.gameweekName}</h3>
              <button
                onClick={() => setPaidPrizesModal({ open: false, gameweekName: '', result: null })}
                className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm"
              >
                Close
              </button>
            </div>

            <div className="p-4 overflow-y-auto space-y-4">
              {paidPrizesModal.result?.sentToTreasury > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-amber-300 text-sm">
                  No participants. Sent to treasury: {formatAmount(paidPrizesModal.result.sentToTreasury)} DIOS
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
                  <p className="text-xs text-gray-400">Total Participants</p>
                  <p className="text-lg font-semibold">{paidPrizesModal.result?.totalParticipants ?? 0}</p>
                </div>
                <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
                  <p className="text-xs text-gray-400">Total Distributed</p>
                  <p className="text-lg font-semibold text-green-400">{formatAmount(paidPrizesModal.result?.totalDistributed)} DIOS</p>
                </div>
                <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
                  <p className="text-xs text-gray-400">Allocated Total</p>
                  <p className="text-lg font-semibold text-yellow-400">{formatAmount(paidPrizesModal.result?.allocatedTotal)} DIOS</p>
                </div>
              </div>

              {Array.isArray(paidPrizesModal.result?.paidPerTournament) && paidPrizesModal.result.paidPerTournament.length > 0 ? (
                <div className="space-y-3">
                  {paidPrizesModal.result.paidPerTournament.map((t) => (
                    <div key={t.tournamentId} className="border border-gray-700 rounded-lg overflow-hidden">
                      <div className="px-4 py-3 bg-gray-900/70 border-b border-gray-700 flex items-center justify-between">
                        <div>
                          <p className="font-semibold">{t.tournamentName || `Tournament #${t.tournamentId}`}</p>
                          <p className="text-xs text-gray-400">
                            Winners paid: {t.totalPaid ?? 0} - Total: {formatAmount(t.totalAmount)} DIOS
                          </p>
                        </div>
                        {t.error && <span className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-300">Error</span>}
                      </div>

                      {t.error ? (
                        <div className="p-3 text-sm text-red-300">{t.error}</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-900/50 text-gray-300">
                              <tr>
                                <th className="text-left p-2">Rank</th>
                                <th className="text-left p-2">User</th>
                                <th className="text-left p-2">User ID</th>
                                <th className="text-left p-2">Score</th>
                                <th className="text-left p-2">Prize</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(t.paidPrizes || []).map((p, idx) => (
                                <tr key={`${t.tournamentId}-${p.userId}-${idx}`} className="border-t border-gray-700/70">
                                  <td className="p-2">{p.rank ?? '-'}</td>
                                  <td className="p-2">{p.username || '-'}</td>
                                  <td className="p-2 text-gray-400">{p.userId}</td>
                                  <td className="p-2">{p.score != null ? Number(p.score).toFixed(2) : '-'}</td>
                                  <td className="p-2 text-green-400">{formatAmount(p.amount)} DIOS</td>
                                </tr>
                              ))}
                              {(t.paidPrizes || []).length === 0 && (
                                <tr>
                                  <td className="p-3 text-gray-400" colSpan={5}>No winners paid for this tournament.</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-400">No tournament payout details available.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 border border-gray-700 my-8">
            <h2 className="text-2xl font-bold mb-4">Create New GameWeek</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                  placeholder="GameWeek #1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Start Time</label>
                  <input
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => setFormData({...formData, startTime: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 mb-2">End Date</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">End Time</label>
                  <input
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => setFormData({...formData, endTime: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-gray-300 mb-2">Categories</label>
                <div className="flex flex-wrap gap-2">
                  {['basic', 'plus', 'rare', 'epic', 'unique'].map((cat) => (
                    <label key={cat} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.categoriesEnabled.includes(cat)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({...formData, categoriesEnabled: [...formData.categoriesEnabled, cat]});
                          } else {
                            setFormData({...formData, categoriesEnabled: formData.categoriesEnabled.filter(c => c !== cat)});
                          }
                        }}
                        className="rounded"
                      />
                      <span className="capitalize">{cat}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="allowLegends"
                  checked={formData.allowLegends}
                  onChange={(e) => setFormData({...formData, allowLegends: e.target.checked})}
                  className="rounded"
                />
                <label htmlFor="allowLegends" className="text-gray-300">Allow Legends</label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="allowDivinities"
                  checked={formData.allowDivinities}
                  onChange={(e) => setFormData({...formData, allowDivinities: e.target.checked})}
                  className="rounded"
                />
                <label htmlFor="allowDivinities" className="text-gray-300">Allow Divinities</label>
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (creating) return; // Prevent double clicks
                  
                  if (!formData.name || !formData.startDate || !formData.endDate || formData.categoriesEnabled.length === 0) {
                    alert('Please fill in all required fields');
                    return;
                  }
                  setCreating(true);
                  try {
                    const result = await adminApi.createGameWeek({
                      name: formData.name,
                      startDate: formData.startDate,
                      endDate: formData.endDate,
                      startTime: formData.startTime || undefined,
                      endTime: formData.endTime || undefined,
                      categoriesEnabled: formData.categoriesEnabled,
                      allowLegends: formData.allowLegends,
                      allowDivinities: formData.allowDivinities,
                      competitions: formData.competitions
                    });
                    setShowCreateModal(false);
                    loadGameWeeks();
                    alert(`GameWeek created successfully! Created ${result.gameweek?.tournaments?.length || 0} tournaments.`);
                  } catch (error) {
                    console.error('Error creating gameweek:', error);
                    alert('Error creating gameweek: ' + (error.response?.data?.error || error.message));
                  } finally {
                    setCreating(false);
                  }
                }}
                disabled={creating}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Multiple GameWeeks Modal */}
      {showCreateMultipleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 border border-gray-700 my-8 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">Create Multiple GameWeeks</h2>
            <div className="flex items-center gap-2 text-amber-200/90 text-sm mb-4">
              <Info className="w-4 h-4 flex-shrink-0" />
              <span>Il fuso orario può differire tra DB locale e produzione (dipende da server/MySQL).</span>
            </div>
            <div className="space-y-4">
              {multipleGameweeks.map((gw, index) => (
                <div key={index} className="border border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold">GameWeek {index + 1}</h3>
                    {multipleGameweeks.length > 1 && (
                      <button
                        onClick={() => setMultipleGameweeks(multipleGameweeks.filter((_, i) => i !== index))}
                        className="text-red-400 hover:text-red-300"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-gray-300 mb-1 text-sm">Name</label>
                      <input
                        type="text"
                        value={gw.name}
                        onChange={(e) => {
                          const updated = [...multipleGameweeks];
                          updated[index].name = e.target.value;
                          setMultipleGameweeks(updated);
                        }}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                        placeholder="GameWeek #1"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-gray-300 mb-1 text-sm">Start Date</label>
                        <input
                          type="date"
                          value={gw.startDate}
                          onChange={(e) => {
                            const updated = [...multipleGameweeks];
                            updated[index].startDate = e.target.value;
                            setMultipleGameweeks(updated);
                          }}
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-300 mb-1 text-sm">Start Time</label>
                        <input
                          type="time"
                          value={gw.startTime}
                          onChange={(e) => {
                            const updated = [...multipleGameweeks];
                            updated[index].startTime = e.target.value;
                            setMultipleGameweeks(updated);
                          }}
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                        />
                      </div>
                      {gw.startDate && (
                        <div className="col-span-2 flex items-center gap-2 text-gray-400 text-xs">
                          <Info className="w-4 h-4 flex-shrink-0" />
                          <span>Se DB è UTC → Italia (CET/CEST): {utcToItalyLabel(gw.startDate, gw.startTime, '00:00', locale || 'it-IT')}</span>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-gray-300 mb-1 text-sm">End Date</label>
                        <input
                          type="date"
                          value={gw.endDate}
                          onChange={(e) => {
                            const updated = [...multipleGameweeks];
                            updated[index].endDate = e.target.value;
                            setMultipleGameweeks(updated);
                          }}
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-300 mb-1 text-sm">End Time</label>
                        <input
                          type="time"
                          value={gw.endTime}
                          onChange={(e) => {
                            const updated = [...multipleGameweeks];
                            updated[index].endTime = e.target.value;
                            setMultipleGameweeks(updated);
                          }}
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                        />
                      </div>
                      {gw.endDate && (
                        <div className="col-span-2 flex items-center gap-2 text-gray-400 text-xs">
                          <Info className="w-4 h-4 flex-shrink-0" />
                          <span>Se DB è UTC → Italia (CET/CEST): {utcToItalyLabel(gw.endDate, gw.endTime, '23:59', locale || 'it-IT')}</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-1 text-sm">Categories</label>
                      <div className="flex flex-wrap gap-2">
                        {['basic', 'plus', 'rare', 'epic', 'unique'].map((cat) => (
                          <label key={cat} className="flex items-center space-x-1 cursor-pointer text-sm">
                            <input
                              type="checkbox"
                              checked={gw.categoriesEnabled.includes(cat)}
                              onChange={(e) => {
                                const updated = [...multipleGameweeks];
                                if (e.target.checked) {
                                  updated[index].categoriesEnabled = [...updated[index].categoriesEnabled, cat];
                                } else {
                                  updated[index].categoriesEnabled = updated[index].categoriesEnabled.filter(c => c !== cat);
                                }
                                setMultipleGameweeks(updated);
                              }}
                              className="rounded"
                            />
                            <span className="capitalize">{cat}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-1 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={gw.allowLegends}
                          onChange={(e) => {
                            const updated = [...multipleGameweeks];
                            updated[index].allowLegends = e.target.checked;
                            setMultipleGameweeks(updated);
                          }}
                          className="rounded"
                        />
                        <span>Allow Legends</span>
                      </label>
                      <label className="flex items-center space-x-1 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={gw.allowDivinities}
                          onChange={(e) => {
                            const updated = [...multipleGameweeks];
                            updated[index].allowDivinities = e.target.checked;
                            setMultipleGameweeks(updated);
                          }}
                          className="rounded"
                        />
                        <span>Allow Divinities</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
              <button
                onClick={() => setMultipleGameweeks([...multipleGameweeks, {
                  name: '',
                  startDate: '',
                  endDate: '',
                  startTime: '',
                  endTime: '',
                  categoriesEnabled: ['basic', 'plus', 'rare', 'epic', 'unique'],
                  allowLegends: true,
                  allowDivinities: false
                }])}
                className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg flex items-center justify-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Add Another GameWeek</span>
              </button>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => {
                  setMultipleGameweeks([{
                    name: '',
                    startDate: '',
                    endDate: '',
                    startTime: '',
                    endTime: '',
                    categoriesEnabled: ['basic', 'plus', 'rare', 'epic', 'unique'],
                    allowLegends: true,
                    allowDivinities: false
                  }]);
                  setShowCreateMultipleModal(false);
                }}
                disabled={creating}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // Validate all gameweeks
                  for (const gw of multipleGameweeks) {
                    if (!gw.name || !gw.startDate || !gw.endDate || gw.categoriesEnabled.length === 0) {
                      alert('Please fill in all required fields for all gameweeks');
                      return;
                    }
                  }
                  setCreating(true);
                  try {
                    const result = await adminApi.generateMultipleGameWeeks(multipleGameweeks);
                    setShowCreateMultipleModal(false);
                    setMultipleGameweeks([{
                      name: '',
                      startDate: '',
                      endDate: '',
                      startTime: '',
                      endTime: '',
                      categoriesEnabled: ['basic', 'plus', 'rare', 'epic', 'unique'],
                      allowLegends: true,
                      allowDivinities: false
                    }]);
                    loadGameWeeks();
                    alert(`Successfully created ${result.created} gameweek(s)!${result.failed > 0 ? ` ${result.failed} failed.` : ''}`);
                  } catch (error) {
                    console.error('Error creating multiple gameweeks:', error);
                    alert('Error creating gameweeks: ' + (error.response?.data?.error || error.message));
                  } finally {
                    setCreating(false);
                  }
                }}
                disabled={creating}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50"
              >
                {creating ? 'Creating...' : `Create ${multipleGameweeks.length} GameWeek(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGameWeeks;

